import React from "react";
import imageVariants, { ImageVariantEntry } from "../../resources/imageVariants";

/**
 * <ResponsiveImage> — `srcset`/`sizes` for the images we serve from the
 * `getlife-bucket-1` GCS bucket.
 *
 * Phase 5 of the PageSpeed plan (dockerize-cms/ai-cms/reports/pagespeed-plan-2026-09-14.md,
 * diagnosis 1.3 item 5): the bucket had zero responsive variants, so the mobile
 * home downloaded ~1100 px originals for 314-412 px slots (PSI: 268 KiB wasted).
 *
 * The variants live next to the original, in a `w<width>` folder:
 *
 *     public/life5-web/card-familia.webp          <- original (1056 px)
 *     public/life5-web/w400/card-familia.webp     <- variant
 *     public/life5-web/w800/card-familia.webp     <- variant
 *
 * They are produced by `dockerize-cms/ai-cms/scripts/gen-image-variants.py`,
 * which also regenerates `src/resources/imageVariants.ts` — the allowlist of
 * bucket paths that actually HAVE variants, with the original's intrinsic size.
 *
 * ⚠️ TWO GATES, both must pass before a `srcset` is emitted
 * ---------------------------------------------------------
 * 1. The image must be in `imageVariants` (the generated allowlist).
 * 2. `GATSBY_IMAGE_VARIANTS` must be `1` at BUILD time.
 *
 * Gate 2 exists because uploading to the shared bucket is gated on Carlos'
 * approval (plan section 4), and the upload has NOT happened yet. This lets the
 * PR be reviewed and merged before the objects exist without ever pointing a
 * `srcset` at a 404: with the flag off (the default) every image renders as the
 * exact same plain `<img>` it renders today. After the variants are uploaded
 * with
 *
 *     gsutil -m cp -h "Cache-Control:public, max-age=31536000, immutable" ...
 *
 * set `GATSBY_IMAGE_VARIANTS=1` in the Jenkins build environment and rebuild.
 * To roll back, unset the flag and rebuild — no code change needed.
 *
 * Anything not in the allowlist always renders the plain `<img>`, so this
 * component is safe to use for Strapi-fed images whose URL is unknown at build
 * time.
 */

const BUCKET_BASE = "https://storage.googleapis.com/getlife-bucket-1/";

/** Build-time flag — see the "TWO GATES" note above. Default: off. */
export const IMAGE_VARIANTS_ENABLED = process.env.GATSBY_IMAGE_VARIANTS === "1";

export interface ResponsiveImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "srcSet"> {
  src?: string | null;
  alt?: string;
  /** Required for a srcset to be useful; ignored when no variants apply. */
  sizes?: string;
  /**
   * `fetchPriority` is not in React 18's typings but is forwarded verbatim to
   * the DOM, which is how the existing hero markup already sets it.
   */
  fetchPriority?: "high" | "low" | "auto";
}

/**
 * `https://storage.googleapis.com/getlife-bucket-1/public/life5-web/a.webp`
 *   -> `public/life5-web/a.webp`
 * Returns null for anything that is not a bucket URL (Strapi uploads, local
 * assets, data URIs…).
 */
export const toBucketObjectPath = (src?: string | null): string | null => {
  if (!src || !src.startsWith(BUCKET_BASE)) return null;
  return src.slice(BUCKET_BASE.length).split("?")[0].split("#")[0];
};

/** Looks the path up raw and percent-decoded, since Strapi URLs can be encoded. */
export const lookupVariants = (src?: string | null): ImageVariantEntry | null => {
  const objectPath = toBucketObjectPath(src);
  if (!objectPath) return null;
  if (imageVariants[objectPath]) return imageVariants[objectPath];
  try {
    const decoded = decodeURIComponent(objectPath);
    if (decoded !== objectPath && imageVariants[decoded]) return imageVariants[decoded];
  } catch {
    /* malformed escape sequence — fall through to "no variants" */
  }
  return null;
};

/** `public/life5-web/a.webp` + 400 -> the full w400 URL. */
const variantUrl = (objectPath: string, width: number): string => {
  const slash = objectPath.lastIndexOf("/");
  const dir = objectPath.slice(0, slash);
  const name = objectPath.slice(slash + 1);
  return `${BUCKET_BASE}${dir}/w${width}/${name}`;
};

/**
 * `w400 400w, w800 800w, <original> 1100w` — the original is always the
 * largest candidate, so a wide viewport still gets full quality.
 */
export const buildSrcSet = (src: string, entry: ImageVariantEntry): string => {
  const objectPath = toBucketObjectPath(src) as string;
  const candidates = entry.widths
    .filter((w) => w < entry.width)
    .map((w) => `${variantUrl(objectPath, w)} ${w}w`);
  candidates.push(`${src} ${entry.width}w`);
  return candidates.join(", ");
};

const ResponsiveImage = ({
  src,
  alt = "",
  sizes,
  width,
  height,
  ...rest
}: ResponsiveImageProps) => {
  const entry = IMAGE_VARIANTS_ENABLED ? lookupVariants(src) : null;

  // No variants (or the flag is off): the same plain <img> as before this
  // component existed — same src, alt, classes, dimensions and loading. Only
  // the order the attributes are serialised in can differ, which browsers and
  // crawlers do not care about.
  if (!entry || !src) {
    return <img src={src || undefined} alt={alt} width={width} height={height} {...rest} />;
  }

  return (
    <img
      src={src}
      srcSet={buildSrcSet(src, entry)}
      sizes={sizes}
      alt={alt}
      width={width ?? entry.width}
      height={height ?? entry.height}
      {...rest}
    />
  );
};

export default ResponsiveImage;
